import { useState } from 'react';
import { MdBlock, MdChevronRight } from 'react-icons/md';
import SideDrawer from '../../../../organisms/SideDrawer';
import SearchFilter from '../../../../atoms/SearchFilter';
import type { CompositionOption } from '../useCollectionCompositions';
import { CHANNEL_LABEL, CollectionChannel } from '../collectionModel';

export type CompositionPick = { skip: true } | { skip: false; option: CompositionOption };

interface CompositionPickerDrawerProps {
    open: boolean;
    options: CompositionOption[];
    selectedSendId: string | null;
    onPick: (pick: CompositionPick) => void;
    onClose: () => void;
}

const NO_GROUP_LABEL = 'Sem grupo';

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const groupOf = (option: CompositionOption) => option.groupName || NO_GROUP_LABEL;

const groupByName = (options: CompositionOption[]): [string, CompositionOption[]][] => {
    const groups = new Map<string, CompositionOption[]>();
    options.forEach(option => groups.set(groupOf(option), [...(groups.get(groupOf(option)) ?? []), option]));
    return Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b, 'pt-BR'))
        .map(([name, list]) => [name, [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))]);
};

const PickRow = ({ title, hint, selected, icon, onPick }: {
    title: string;
    hint?: string;
    selected: boolean;
    icon?: React.ReactNode;
    onPick: () => void;
}) => (
    <li>
        <div role="button" tabIndex={0} onClick={onPick} onKeyDown={e => { if (e.key === 'Enter') onPick(); }}
            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700 ${selected ? 'bg-blue-50 dark:bg-slate-700' : ''}`}>
            {icon}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-100 truncate">{title}</p>
                {hint && <p className="text-[11px] text-gray-500 truncate">{hint}</p>}
            </div>
            <button type="button" aria-label={`Escolher ${title}`} onClick={e => { e.stopPropagation(); onPick(); }}
                className="p-1 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-slate-600">
                <MdChevronRight size={20} />
            </button>
        </div>
    </li>
);

const CompositionPickerDrawer = ({ open, options, selectedSendId, onPick, onClose }: CompositionPickerDrawerProps) => {
    const [term, setTerm] = useState('');
    const matches = options.filter(o => o.active && normalize(o.name).includes(normalize(term.trim())));
    const groups = groupByName(matches);

    return (
        <SideDrawer open={open} width="520px" title="Escolher composição da mensagem" onClose={onClose}>
            <div className="space-y-4">
                <SearchFilter value={term} onChange={setTerm} placeholder="Buscar composição pelo nome" />
                <ul className="rounded-lg border border-gray-200 dark:border-slate-700">
                    <PickRow title="Não enviar" hint="Este destinatário não recebe mensagem neste caso" selected={false}
                        icon={<MdBlock size={18} className="text-gray-400" />} onPick={() => onPick({ skip: true })} />
                </ul>
                {groups.length === 0 && <p className="px-3 py-2 text-xs italic text-gray-400">Nenhuma composição ativa</p>}
                {groups.map(([name, list]) => (
                    <section key={name}>
                        <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">{name} ({list.length})</h4>
                        <ul className="rounded-lg border border-gray-200 dark:border-slate-700">
                            {list.map(option => (
                                <PickRow key={option.sendId} title={option.name}
                                    hint={`${CHANNEL_LABEL[option.channel as CollectionChannel] ?? option.channel} · ${option.sendId}`}
                                    selected={option.sendId === selectedSendId}
                                    onPick={() => onPick({ skip: false, option })} />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        </SideDrawer>
    );
};

export default CompositionPickerDrawer;
